import { useState, useEffect } from "react"
import { Leaf, Heart, Shield, Award, Users, Globe, ArrowRight, Loader2, Facebook, Instagram, Twitter, Linkedin } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Link } from "react-router-dom"
import Seo from "@/seo/seo"
import { routeSeoConfig } from "@/utils/seo-config"
import axios from "@/utils/axios"
import CommunityModal from "./community-modal"

// Icon mapping
const iconMap = {
  Leaf,
  Heart,
  Shield,
  Award,
  Users,
  Globe,
}

export default function AboutUs() {
  const [aboutData, setAboutData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isCommunityModalOpen, setIsCommunityModalOpen] = useState(false)

  useEffect(() => {
    if (window.location.hash === "#join-community") {
      const timer = setTimeout(() => {
        setIsCommunityModalOpen(true)
      }, 800)
      return () => clearTimeout(timer)
    }
  }, [])

  useEffect(() => {
    const fetchAboutUs = async () => {
      try {
        const res = await axios.get("/about")
        setAboutData(res.data.aboutUs)
      } catch (error) {
        console.error("Error fetching about us:", error)
        // Use default data if API fails
        setAboutData(null)
      } finally {
        setLoading(false)
      }
    }
    fetchAboutUs()
  }, [])

  // Default values if API fails or data is loading
  const defaultValues = [
    {
      icon: "Leaf",
      title: "Natural Ingredients",
      description: "We source only the finest natural and organic ingredients from sustainable farms worldwide.",
    },
    {
      icon: "Shield",
      title: "Science-Backed",
      description: "Every formula is developed with clinical research and dermatologist testing for proven results.",
    },
    {
      icon: "Heart",
      title: "Cruelty-Free",
      description: "We never test on animals and are committed to ethical beauty practices.",
    },
    {
      icon: "Globe",
      title: "Sustainable",
      description: "Our packaging is eco-friendly and we support environmental conservation initiatives.",
    },
  ]

  const defaultTeam = [
    {
      name: "Dr. Sarah Chen",
      role: "Founder & Chief Formulator",
      image: "https://placehold.co/600x400",
      bio: "15+ years in dermatology research",
    },
    {
      name: "Marcus Rodriguez",
      role: "Head of Product Development",
      image: "https://placehold.co/600x400",
      bio: "Former L'Oréal senior scientist",
    },
    {
      name: "Emma Thompson",
      role: "Sustainability Director",
      image: "https://placehold.co/600x400",
      bio: "Environmental science expert",
    },
  ]

  const defaultStats = [
    { number: "500K+", label: "Happy Customers" },
    { number: "98%", label: "Satisfaction Rate" },
    { number: "50+", label: "Countries Served" },
    { number: "15", label: "Years Experience" },
  ]

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  const values = aboutData?.values || defaultValues
  const team = aboutData?.team || defaultTeam
  const stats = aboutData?.stats || defaultStats
  const heroTitle = aboutData?.hero_title || "Transforming Skin with Nature's Power"
  const heroSubtitle = aboutData?.hero_subtitle || "Founded in 2019, Fayoz has been pioneering natural skincare solutions that deliver real results. Our mission is to help everyone achieve healthy, radiant skin through the power of nature and science."
  const storyTitle = aboutData?.story_title || "Our Story"
  const storyContent = aboutData?.story_content || "Fayoz was born from a simple belief: everyone deserves healthy, beautiful skin."
  const storyImage = aboutData?.story_image || "/natural-skincare-laboratory-with-plants-and-scient.jpg"
  const storyAwardTitle = aboutData?.story_award_title || "Award Winning"
  const storyAwardSubtitle = aboutData?.story_award_subtitle || "Best Natural Skincare 2023"
  const valuesTitle = aboutData?.values_title || "Our Values"
  const valuesSubtitle = aboutData?.values_subtitle || "These core principles guide everything we do, from product development to customer service."
  const teamTitle = aboutData?.team_title || "Meet Our Team"
  const teamSubtitle = aboutData?.team_subtitle || "The passionate experts behind Fayoz's innovative skincare solutions."
  const missionTitle = aboutData?.mission_title || "Our Mission"
  const missionContent = aboutData?.mission_content || "To empower everyone to feel confident in their skin by providing natural, effective, and sustainable skincare solutions that are backed by science and crafted with care."
  const ctaTitle = aboutData?.cta_title || "Ready to Transform Your Skin?"
  const ctaSubtitle = aboutData?.cta_subtitle || "Join thousands of satisfied customers who have discovered the power of natural skincare."

  return (
    <>
      <Seo {...routeSeoConfig.about} type="website" />
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-br from-rose-50 via-pink-50 to-orange-50 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-secondary/5"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 text-balance">
              {heroTitle.includes("Nature's Power") ? (
                <>
                  {heroTitle.split("Nature's Power")[0]}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">
                    {" "}Nature's Power
                  </span>
                </>
              ) : (
                heroTitle
              )}
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto text-pretty">
              {heroSubtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {aboutData?.hero_button_text && (
                <Link to={aboutData.hero_button_link || "/shop"}>
                  <Button size="lg" className="bg-primary hover:bg-primary/90 text-white">
                    {aboutData.hero_button_text}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              )}
              {aboutData?.hero_secondary_button_text && (
                <Link to={aboutData.hero_secondary_button_link || "#"}>
                  <Button
                    variant="outline"
                    size="lg"
                    className="border-primary text-primary hover:bg-primary/5 bg-transparent"
                  >
                    {aboutData.hero_secondary_button_text}
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-primary mb-2">{stat.number}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-rose-50/30">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">{storyTitle}</h2>
              <div className="space-y-4 text-gray-600 text-lg text-justify whitespace-pre-line">
                {storyContent.split('\n\n').map((paragraph, idx) => (
                  <p key={idx}>{paragraph}</p>
                ))}
              </div>
            </div>
            <div className="relative">
              <img src={storyImage} alt="Our laboratory" className="rounded-2xl shadow-2xl w-full" />
              {(storyAwardTitle || storyAwardSubtitle) && (
                <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-lg">
                  <div className="flex items-center gap-3">
                    <Award className="w-8 h-8 text-secondary" />
                    <div>
                      {storyAwardTitle && <div className="font-semibold text-gray-900">{storyAwardTitle}</div>}
                      {storyAwardSubtitle && <div className="text-sm text-gray-600">{storyAwardSubtitle}</div>}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{valuesTitle}</h2>
            {valuesSubtitle && (
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                {valuesSubtitle}
              </p>
            )}
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const IconComponent = iconMap[value.icon] || Leaf
              return (
                <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center mx-auto mb-6">
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">{value.title}</h3>
                    <p className="text-gray-600">{value.description}</p>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-gradient-to-br from-rose-50/50 to-pink-50/50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{teamTitle}</h2>
            {teamSubtitle && (
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                {teamSubtitle}
              </p>
            )}
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <Card
                key={index}
                className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 pt-0 pb-0 group overflow-hidden"
              >
                <CardContent className="p-0">
                  <div className="relative overflow-hidden h-80">
                    <img
                      src={member.image || "/placeholder.svg"}
                      alt={member.name}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                    {/* Hover overlay with faded shadow effect */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center justify-end pb-6 px-4">
                      {/* Name and Role */}
                      <h3 className="text-xl font-semibold text-white mb-1 text-center">{member.name}</h3>
                      <p className="text-white/80 font-medium mb-4 text-center">{member.role}</p>
                      {/* Social media icons */}
                      <div className="flex gap-3">
                        {member.facebook && (
                          <a
                            href={member.facebook}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/40 transition-colors duration-200"
                          >
                            <Facebook className="w-5 h-5 text-white" />
                          </a>
                        )}
                        {member.instagram && (
                          <a
                            href={member.instagram}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/40 transition-colors duration-200"
                          >
                            <Instagram className="w-5 h-5 text-white" />
                          </a>
                        )}
                        {member.twitter && (
                          <a
                            href={member.twitter}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/40 transition-colors duration-200"
                          >
                            <Twitter className="w-5 h-5 text-white" />
                          </a>
                        )}
                        {member.linkedin && (
                          <a
                            href={member.linkedin}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/40 transition-colors duration-200"
                          >
                            <Linkedin className="w-5 h-5 text-white" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section id="join-community" className="py-20 bg-gradient-to-r from-primary to-secondary text-white">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">{missionTitle}</h2>
            <p className="text-xl mb-8 opacity-90 text-pretty">
              {missionContent}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="secondary" className="bg-white text-primary hover:bg-gray-100" onClick={() => setIsCommunityModalOpen(true)}>
                <Users className="w-4 h-4 mr-2" />
                Join Our Community
              </Button>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 bg-transparent">
                Learn More About Our Ingredients
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">{ctaTitle}</h2>
          {ctaSubtitle && (
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              {ctaSubtitle}
            </p>
          )}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/shop">
              <Button size="lg" className="bg-primary hover:bg-primary/90 text-white">
                Shop Now
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/contact-us">
              <Button
                variant="outline"
                size="lg"
                className="border-primary text-primary hover:bg-primary/5 bg-transparent"
              >
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
      </div>
      <CommunityModal isOpen={isCommunityModalOpen} setIsOpen={setIsCommunityModalOpen} />
    </>
  )
}
